"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import {
  FileUp,
  X,
  Loader,
  Book,
  Calendar,
  Clock,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
} from "lucide-react"
import { Worker, Viewer } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import { generateQuizWithAi, getAllbooks } from "./api"
import { postTeacherQuizFile, generatePresignedUrl } from "../postFileAws"

const QuizCreationForm = () => {
  const navigate = useNavigate()
  const [title, setTitle] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [topic, setTopic] = useState("")
  const [aiPrompt, setAiPrompt] = useState("")
  const [questionCount, setQuestionCount] = useState(5)
  const [timePerQuestion, setTimePerQuestion] = useState(30)
  const [selectedDifficulty, setSelectedDifficulty] = useState("medium")
  const [pdfFile, setPdfFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLibraryLoading, setIsLibraryLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [refUri, setRefUri] = useState("")
  const selectedClass = useSelector((state) => state.teacherSub.selectedClass)
  const selectedSubject = useSelector((state) => state.teacherSub.selectedSubject)
  const teacherId = useSelector((state) => state.teacherSub.teacherId)
  const classCode = useSelector((state) => state.teacherSub.classCode)
  const [refType, setRefType] = useState("")
  const [refMaterialSource, setRefMaterialSource] = useState("upload")
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false)
  const [books, setBooks] = useState([])
  const [selectedBook, setSelectedBook] = useState(null)
  const [previewBook, setPreviewBook] = useState(null)
  const organisationId = localStorage.getItem("organisationId")

  const classRoomId = localStorage.getItem("classRoomId")

  useEffect(() => {
    const oneWeekFromNow = new Date()
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7)
    setDueDate(oneWeekFromNow.toISOString().split("T")[0])
  }, [])

  const fetchBooks = useCallback(async () => {
    if (!classRoomId) {
      setIsLibraryLoading(false)
      return
    }

    try {
      setIsLibraryLoading(true)
      const response = await getAllbooks(classRoomId)
      if (response.body.data) {
        const booksWithPresignedUrls = await Promise.all(
          response.body.data.map(async (book) => {
            const presignedUrl = await generatePresignedUrl(book.fileUrl)
            return { ...book, presignedUrl }
          }),
        )
        setBooks(booksWithPresignedUrls)
      } else {
        setBooks([])
      }
    } catch (error) {
      console.error("Error fetching books:", error)
      setError("Failed to load books. Please try again.")
    } finally {
      setIsLibraryLoading(false)
    }
  }, [classRoomId])

  const difficultyLevels = [
    { id: "easy", label: "Easy", color: "bg-emerald-500", hoverColor: "hover:bg-emerald-600" },
    { id: "medium", label: "Medium", color: "bg-amber-500", hoverColor: "hover:bg-amber-600" },
    { id: "hard", label: "Hard", color: "bg-rose-500", hoverColor: "hover:bg-rose-600" },
  ]

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 7 * 1024 * 1024) {
        setError("Max limit reached, please import this file from library")
        setPdfFile(null)
        return
      }
      if (file.type === "application/pdf" || file.type.startsWith("image/")) {
        setPdfFile(file)
        const response = await postTeacherQuizFile(file, teacherId, title, "refMaterial")
        console.log(response)
        setRefUri(response.uri)
        setRefType(file.type === "application/pdf" ? "pdf" : "image")
        setError("")
      } else {
        setError("Please upload a PDF or image file")
        setPdfFile(null)
      }
    }
  }

  const handleLibrarySelection = async () => {
    try {
      setIsLibraryModalOpen(true)
      await fetchBooks()
    } catch (error) {
      console.error("Error fetching books:", error)
      setError("Failed to load books from the library")
    }
  }

  const handleBookSelect = (book) => {
    setSelectedBook(book)
    setPreviewBook({ ...book, presignedUrl: book.presignedUrl || book.fileUrl })
  }

  const handleConfirmBook = () => {
    if (selectedBook) {
      setRefUri(selectedBook.fileUri)
      setRefType("pdf")
      setPdfFile(null)
      setIsLibraryModalOpen(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")
    setSuccess("")

    try {
      const quizData = {
        quizTitle: title,
        quizDueDate: dueDate,
        quiz_topic: topic,
        quizDescription: "",
        teacherId: teacherId,
        classCode: classCode,
        classRoomId: classRoomId,
        questionNumber: questionCount,
        questionDifficulty: selectedDifficulty,
        referenceType: refType || "",
        refMaterial: refUri || "",
        userPrompt: aiPrompt || "",
        organisationId: organisationId,
        timePerQuestion: timePerQuestion || "30",
      }

      console.log("Scheduling quiz creation")
      const response = await generateQuizWithAi(quizData)
      console.log("Server response:", response)

      if (response && response.success === true) {
        console.log("Quiz creation scheduled successfully")
        setSuccess("Quiz is being created!")
        setTimeout(() => navigate("/quiz"), 2000)
      } else {
        throw new Error(response?.error || "Failed to create quiz")
      }
    } catch (err) {
      console.error("Error in handleSubmit:", err)
      setError(err.message || "Failed to create quiz")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-100 p-4 md:p-6 lg:p-8 -mt-5">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 transform hover:shadow-2xl">
        <div className="flex items-center p-4 md:p-6 border-b border-gray-200">
          <a href="/quiz" className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" />
            <span className="text-sm font-medium">Quizes</span>
          </a>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 lg:p-8 space-y-8 -mt-6">
          {/* Basic Information Section */}
          <div className="bg-indigo-50 p-5 rounded-xl">
    

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Quiz Title
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    placeholder="Enter title"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="topic" className="block text-sm font-medium text-gray-700">
                  Topic
                </label>
                <input
                  type="text"
                  id="topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  required
                  placeholder="Enter the quiz topic"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
                  Due Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="dueDate"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  />
                  <Calendar className="absolute right-3 top-3 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Class-Subject</label>
                <div className="relative">
                  <input
                    type="text"
                    value={`${selectedClass}-${selectedSubject}`}
                    readOnly
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                    placeholder="Class-Subject"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* AI Prompt Section */}
          <div className="space-y-2">
            <label htmlFor="aiPrompt" className="block text-sm font-medium text-gray-700 flex items-center">
              AI Prompt <span className="text-gray-500 ml-2 text-xs">(Optional)</span>
            </label>
            <textarea
              id="aiPrompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Enter specific instructions for AI to generate your quiz questions..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all h-32 resize-none"
            />
          </div>

          {/* Advanced Settings Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Reference Material Card */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-lg">
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4">
                <h3 className="text-white font-medium">Reference Material</h3>
                <p className="text-indigo-100 text-sm">Add supporting documents</p>
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <ToggleSwitch
                    isOn={refMaterialSource === "library"}
                    handleToggle={() => setRefMaterialSource(refMaterialSource === "upload" ? "library" : "upload")}
                    onLabel="Library"
                    offLabel="Upload"
                  />
                </div>

                {refMaterialSource === "upload" ? (
                  <div className="mt-4">
                    <label htmlFor="fileInput" className="cursor-pointer block">
                      <div className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors">
                        <FileUp className="w-8 h-8 text-indigo-400 mb-2" />
                        <p className="text-sm text-indigo-500">Click to upload PDF or Image</p>
                        <p className="text-xs text-gray-500 mt-1">Max size: 7MB</p>
                      </div>
                    </label>
                    <input
                      id="fileInput"
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      accept=".pdf,image/*"
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleLibrarySelection}
                    className="w-full mt-4 px-4 py-3 border border-indigo-300 rounded-lg shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all flex items-center justify-center"
                  >
                    <Book className="w-4 h-4 mr-2" />
                    Select from Library
                  </button>
                )}

                {(pdfFile || (refMaterialSource === "library" && selectedBook)) && (
                  <div className="mt-4 flex items-center justify-between bg-indigo-50 p-3 rounded-lg">
                    <span className="text-sm text-indigo-700 truncate max-w-[80%]">
                      {pdfFile ? pdfFile.name : selectedBook ? selectedBook.fileTitle : "Selected book"}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setPdfFile(null)
                        setRefUri("")
                        setSelectedBook(null)
                      }}
                      className="text-indigo-500 hover:text-indigo-700 bg-white rounded-full p-1 shadow-sm"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Difficulty Level Card */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-lg">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4">
                <h3 className="text-white font-medium">Difficulty Level</h3>
                <p className="text-purple-100 text-sm">Set challenge level for students</p>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 gap-3">
                  {difficultyLevels.map((level) => (
                    <button
                      key={level.id}
                      type="button"
                      onClick={() => setSelectedDifficulty(level.id)}
                      className={`flex items-center justify-center px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                        selectedDifficulty === level.id
                          ? `${level.color} text-white shadow-md`
                          : `bg-gray-100 text-gray-700 hover:bg-gray-200`
                      }`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Quiz Settings Card */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-lg">
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-4">
                <h3 className="text-white font-medium">Quiz Settings</h3>
                <p className="text-green-100 text-sm">Configure quiz parameters</p>
              </div>

              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <label htmlFor="questionCount" className="block text-sm font-medium text-gray-700">
                    Number of Questions
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="questionCount"
                      min="1"
                      value={questionCount}
                      onChange={(e) => setQuestionCount(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                    />
                    <HelpCircle className="absolute right-3 top-3 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="timePerQuestion" className="block text-sm font-medium text-gray-700">
                    Time per Question (seconds)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="timePerQuestion"
                      min="1"
                      value={timePerQuestion}
                      onChange={(e) => setTimePerQuestion(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                    />
                    <Clock className="absolute right-3 top-3 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-md">
              <div className="flex">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-700 font-medium">Success!</p>
                  <p className="text-green-600 text-sm">{success}</p>
                  <p className="text-green-600 text-sm mt-1">Redirecting to quizzes page...</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all shadow-md font-medium text-lg disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader className="animate-spin mr-2" />
                  Creating Quiz...
                </div>
              ) : (
                "Create Quiz"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Library Modal */}
      {isLibraryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Select a Book from Library</h3>
              <button
                onClick={() => setIsLibraryModalOpen(false)}
                className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-grow flex flex-col md:flex-row overflow-hidden">
              <div className="w-full md:w-1/3 overflow-y-auto border-b md:border-b-0 md:border-r border-gray-200">
                <div className="p-2">
                  {books.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                      <Book className="w-8 h-8 text-gray-300 mb-2" />
                      <p>No books available</p>
                    </div>
                  ) : (
                    books.map((book) => (
                      <div
                        key={book.fileUrl}
                        className={`p-4 cursor-pointer rounded-lg transition-colors ${
                          selectedBook === book
                            ? "bg-indigo-100 border border-indigo-200"
                            : "hover:bg-gray-50 border border-transparent"
                        }`}
                        onClick={() => handleBookSelect(book)}
                      >
                        <div className="flex items-center">
                          <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                            <Book className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="overflow-hidden">
                            <p className="font-medium text-gray-800 truncate">{book.fileTitle}</p>
                            <p className="text-sm text-gray-500 truncate">{book.authorName}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="w-full md:w-2/3 p-4 overflow-hidden flex flex-col h-[50vh] md:h-auto">
                {previewBook ? (
                  <>
                    <h4 className="text-lg font-semibold mb-2 text-indigo-800">{previewBook.fileTitle}</h4>
                    <div className="flex-grow overflow-hidden border border-gray-200 rounded-lg">
                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <Viewer fileUrl={previewBook.presignedUrl || previewBook.fileUrl} />
                      </Worker>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <Book className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-lg">Select a book to preview</p>
                    <p className="text-sm text-gray-400 mt-1">Choose from the list on the left</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setIsLibraryModalOpen(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBook}
                disabled={!selectedBook}
                className={`px-6 py-2 rounded-lg font-medium ${
                  selectedBook
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                } transition-colors`}
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLibraryLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl flex flex-col items-center">
            <Loader className="animate-spin text-indigo-600 mb-4" size={48} />
            <p className="text-lg font-semibold text-gray-800">Loading library...</p>
            <p className="text-gray-500 text-sm mt-1">Please wait while we fetch your books</p>
          </div>
        </div>
      )}
    </div>
  )
}

const ToggleSwitch = ({ isOn, handleToggle, onLabel = "On", offLabel = "Off" }) => {
  return (
    <div className="flex items-center">
      <span className={`mr-2 text-sm font-medium ${!isOn ? "text-indigo-600" : "text-gray-500"}`}>{offLabel}</span>
      <div
        onClick={handleToggle}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer
          ${isOn ? "bg-indigo-600" : "bg-gray-200"}
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm
            ${isOn ? "translate-x-6" : "translate-x-1"}
          `}
        />
      </div>
      <span className={`ml-2 text-sm font-medium ${isOn ? "text-indigo-600" : "text-gray-500"}`}>{onLabel}</span>
    </div>
  )
}

export default QuizCreationForm
